import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Key, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { teamAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface TeamPreview {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    registrationDeadline: string;
    status: string;
    maxTeamSize: number;
  };
  leader: {
    firstName: string;
    lastName: string;
    email: string;
  };
  members: Array<{
    user: {
      firstName: string;
      lastName: string;
    };
    role: string;
  }>;
  selectedProblemStatement?: {
    title: string;
    category: string;
    difficulty: string;
  };
  assignedProblemStatement?: {
    title: string;
    category: string;
    difficulty: string;
  };
  status: string;
  joinCode: string;
}

const TeamJoin: React.FC = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();
  
  const [joinCode, setJoinCode] = useState('');
  const [teamPreview, setTeamPreview] = useState<TeamPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userApplicationStatus, setUserApplicationStatus] = useState<{
    hasApplied: boolean;
    teamName?: string;
    status?: string;
  } | null>(null);

  // Check user's application status on component mount
  useEffect(() => {
    checkUserApplicationStatus();
  }, []);

  const checkUserApplicationStatus = async () => {
    try {
      // Get user's teams to check if they're already a member or have applied
      const myTeams = await teamAPI.getMyTeams();
      const hasApplied = myTeams.length > 0;
      
      if (hasApplied) {
        const team = myTeams[0]; // User can only be in one team
        setUserApplicationStatus({
          hasApplied: true,
          teamName: team.name,
          status: team.status
        });
      } else {
        setUserApplicationStatus({
          hasApplied: false
        });
      }
    } catch (err) {
      // If there's an error, assume user hasn't applied
      setUserApplicationStatus({
        hasApplied: false
      });
    }
  };

  const handlePreviewTeam = async () => {
    if (!joinCode.trim()) {
      setErrorMessage('Please enter a join code');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      const response = await teamAPI.getTeamByJoinCode(joinCode.trim());
      setTeamPreview(response);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Invalid join code');
      setTeamPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async () => {
    if (!teamPreview) return;

    try {
      setJoining(true);
      await teamAPI.joinTeam(joinCode.trim());
      success('Successfully joined the team!');
      navigate(`/teams/${teamPreview._id}`);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to join team');
    } finally {
      setJoining(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'forming': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const canJoinTeam = () => {
    if (!teamPreview) return false;
    
    // Check if user has already applied to or is a member of another team
    if (userApplicationStatus?.hasApplied) {
      return false;
    }
    
    // Check if team is full
    if (teamPreview.members.length >= teamPreview.hackathon.maxTeamSize) {
      return false;
    }
    
    // Check if team status allows joining (forming, pending_approval, approved, active)
    if (!['forming', 'pending_approval', 'approved', 'active'].includes(teamPreview.status)) {
      return false;
    }
    
    // Check if registration deadline has passed
    const registrationDeadline = new Date(teamPreview.hackathon.registrationDeadline);
    const now = new Date();
    if (now > registrationDeadline) {
      return false;
    }
    
    return true;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Join a Team</h1>
        <p className="text-gray-600">
          Join code functionality is temporarily disabled. Please contact the team leader directly.
        </p>
      </div>

      {/* Warning if user has already applied */}
      {userApplicationStatus?.hasApplied && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="font-medium">Already Part of a Team</p>
                <p className="text-sm">
                  You are already a member of <span className="font-semibold">{userApplicationStatus.teamName}</span>. 
                  You can only be part of one team at a time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Join Code Disabled Notice */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-blue-800">
            <AlertCircle className="h-5 w-5" />
            <div>
              <p className="font-medium">Join Code Functionality Temporarily Disabled</p>
              <p className="text-sm">
                The join code feature is currently unavailable. Please contact the team leader directly to join a team.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamJoin; 